import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import useForm from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { reduce, each, filter, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { Icon } from 'semantic-ui-react';
import { Grid } from '@material-ui/core';
import { updateCascoForm } from '../../../../redux/modules/cascoClaim';
import Validation from '../../../../components/Validation/Validation';
import {
  ACC_INITIAL_ADDRESS,
  ARMENIA_ISO3,
  CAR_INITIAL_ADDRESS,
  DAMAGED_CAR_OPTIONS,
  PROCEEDINGS,
} from '../../../../constants';
import { selectSortedCountries, selectSortedRAStates } from '../../../../redux/modules/countries';
import {
  getAccCityVillages,
  getCarCityVillages,
  sortedAccCityVillages,
  sortedCarCityVillages,
} from '../../../../redux/modules/cmtplAdresses';
import {
  ACCIDENT_INFO_FIELDS,
  ACCIDENT_OTHER_CARS_OPTIONS,
} from '../../options';
import DateInputFieldWithValidation from '../../../../components/ValidationFormElements/DateInputFieldWithValidation';
import TimeInputWithValidation from '../../../../components/ValidationFormElements/TimeInputWithValidation';
import SelectWithValidation from '../../../../components/ValidationFormElements/SelectWithValidation';
import TextInputFieldWithValidation from '../../../../components/ValidationFormElements/TextInputFieldWithValidation';
import ArmenianRegionSelectOptions from '../../../../components/FormComponents/ContactInfo/ArmenianRegionSelectOptions';
import MainRadio from '../../../../components/MUI/MainRadio';
import ExistingContracts from '../../../../components/ClaimFormComponents/ExistingContracts';
import ClaimStepNavigationWrapper from '../../../../components/ClaimFormComponents/ClaimStepNavigationWrapper';
import useDeviceResize from '../../../../hooks/useDeviceResize';
import {
  addVehicle,
  bindPolicies,
  changeAccidentState,
  changeCarCountry,
  changeCarState,
  changeVehicleData,
  handleUpdateDisabledInContracts,
  sortContractByIsDisabled,
} from '../../../../helpers/claims';
import VehicleRow from '../../../../components/ClaimFormComponents/VehicleRow';
import accidentInfoSchema, {
  getRowSchema,
} from '../../../../validations/claimValidationSchema/casco/accidentInfoSchema';
import useRegisterValidationFields from '../../../../hooks/useRegisterValidationFields';
import useUnRegisterValidationFields from '../../../../hooks/useUnRegisterValidationFields';
import { findCountriesByISO3, findIsYerevan, isArmenianCountry } from '../../../../helpers/countryHelper';
import { NON_DIGITS_PATTERN } from '../../../../constants/patterns';
import { getMenuLabelWithTranslation } from '../../../../helpers/selectHelpers';
import FormSection from '../../../../components/FormComponents/FormSection';
import { handleAccidentCountryChange } from '../../../../helpers/claims/claimApplicationHelpers';

import AccidentInfoIcon from '../../../../img/formSectionIcons/accidentInfo.svg';
import AccidentPlaceIcon from '../../../../img/formSectionIcons/accidentPlace.svg';
import RunAndDriveIcon from '../../../../img/formSectionIcons/runAndDrive.svg';
import DamageInfoIcon from '../../../../img/formSectionIcons/damageInfo.svg';
import ProceedingsIcon from '../../../../img/formSectionIcons/proceedings.svg';
import OtherCarsIcon from '../../../../img/formSectionIcons/otherCars.svg';

import useStyles from '../../../CompensationContainer/useStyles';
import styles from '../../../CompensationContainer/Steps/steps.module.scss';

const getSchema = (vehicles) => reduce(
  vehicles, (acc, v) => acc.concat(
    getRowSchema(v.id),
  ), accidentInfoSchema,
);

const CascoAccidentInfoStep = ({
  step,
  goNext,
  goPrevious,
  params: { setStepNavigationBox, formLanguage, scrollTo },
}) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const isDeviceLgSize = useDeviceResize('lg');

  const { accidentInfo: initialData } = useSelector((store) => store.cascoClaim, shallowEqual);

  const RAStates = useSelector((state) => selectSortedRAStates(state, i18n.language));
  const accCityVillages = useSelector((state) => sortedAccCityVillages(state, i18n.language));
  const carCityVillages = useSelector((state) => sortedCarCityVillages(state, i18n.language));
  const countries = useSelector((state) => selectSortedCountries(state, i18n.language));

  const [accDate, setAccDate] = useState(initialData && initialData.accidentDate);
  const [accTime, setAccTime] = useState(initialData && initialData.accidentTime);

  const policies = useMemo(() => bindPolicies(initialData, accDate), [initialData, accDate]);
  const [existingContracts, setExistingContracts] = useState(sortContractByIsDisabled(policies));
  const [showContractNotExistError, setShowContractNotExistError] = useState(false);
  const [contract, setContract] = useState((initialData && initialData.contract) || null);
  const [accAddress, setAccAddress] = useState(
    initialData.accCountry
      ? {
        isAccInArmenia: isArmenianCountry(initialData.accCountry),
        accCountry: initialData.accCountry,
        accState: initialData.accState,
        accVillage: initialData.accVillage,
      }
      : {
        ...ACC_INITIAL_ADDRESS,
        accCountry: findCountriesByISO3([ARMENIA_ISO3], countries),
      },
  );
  const [carAddress, setCarAddress] = useState(
    initialData.carCountry
      ? {
        isCarInArmenia: isArmenianCountry(initialData.carCountry),
        carCountry: initialData.carCountry,
        carState: initialData.carState,
        carVillage: initialData.carVillage,
      }
      : CAR_INITIAL_ADDRESS,
  );
  const [accVillages, setAccVillages] = useState([]);
  const [carVillages, setCarVillages] = useState([]);
  const [damageAmount, setDamageAmount] = useState(initialData.damageAmount || null);
  const [damagedParts, setDamagedParts] = useState(initialData.damagedParts || '');
  const [accidentDetails, setAccidentDetails] = useState(initialData.accidentDetails || '');
  const [damageLevel, setDamageLevel] = useState(
    initialData.damageLevel || DAMAGED_CAR_OPTIONS[0].value,
  );
  const [isExistOtherCar, setIsExistOtherCar] = useState(
    initialData.isExistOtherCar || ACCIDENT_OTHER_CARS_OPTIONS[0].value,
  );
  const [proceedingStatus, setProceedingStatus] = useState(initialData.proceedingStatus || null);
  const [vehicles, setVehicles] = useState((initialData && initialData.carList) || []);
  const [schema, setSchema] = useState(getSchema(vehicles));
  const { manufacturers } = useSelector((store) => store.carMarks, shallowEqual);
  const accIsYerevan = useMemo(() => findIsYerevan(accAddress.accState), [accAddress.accState]);
  const carIsYerevan = useMemo(() => findIsYerevan(carAddress.carState), [carAddress.carState]);

  const {
    handleSubmit,
    register,
    unregister,
    setValue,
    errors,
    clearError,
    triggerValidation,
    getValues,
  } = useForm({
    validationSchema: schema,
    mode: 'onBlur',
  });

  useRegisterValidationFields(ACCIDENT_INFO_FIELDS, register);
  useUnRegisterValidationFields(ACCIDENT_INFO_FIELDS, unregister);

  useEffect(() => {
    setValue('proceedingStatus', proceedingStatus, !!proceedingStatus);
  }, [proceedingStatus, setValue]);

  useEffect(() => {
    setValue('damageLevel', damageLevel);
  }, [damageLevel, setValue]);

  useEffect(() => {
    setValue('isExistOtherCar', isExistOtherCar);
  }, [isExistOtherCar, setValue]);

  useEffect(() => {
    each(initialData, (value, key) => {
      if (value) {
        setValue(key, value, !!value);
      }
    });
  }, [initialData, setValue]);

  useEffect(() => {
    setValue('formLanguage', formLanguage);
  }, [formLanguage, setValue]);

  useEffect(() => {
    vehicles.forEach(({ id, stateNumber, manufacturer, model }) => {
      register({ name: `stateNumber_${id}` });
      register({ name: `manufacturer_${id}` });
      register({ name: `model_${id}` });

      setValue(`stateNumber_${id}`, stateNumber, !!stateNumber);
      setValue(`manufacturer_${id}`, manufacturer, !!manufacturer);
      setValue(`model_${id}`, model, !!model);
    });
  }, [vehicles, register, setValue]);

  useEffect(() => {
    setAccVillages(accCityVillages);
  }, [accCityVillages]);

  useEffect(() => {
    setCarVillages(carCityVillages);
  }, [carCityVillages]);

  useEffect(() => {
    if (accAddress.accState) {
      dispatch(getAccCityVillages(accAddress.accState.id));
    }
  }, [dispatch, accAddress.accState]);

  useEffect(() => {
    if (carAddress.carState) {
      dispatch(getCarCityVillages(carAddress.carState.id));
    }
  }, [dispatch, carAddress.carState]);

  useEffect(() => {
    setValue('damageAmount', damageAmount, !!damageAmount);
  }, [setValue, damageAmount]);

  useEffect(() => {
    setValue('vehicles', vehicles);
  }, [setValue, vehicles]);

  const updateContract = useCallback(
    (existingContract) => {
      setContract(existingContract);
      setValue('contract', existingContract, true);
    },
    [setValue],
  );

  useEffect(() => {
    if (policies.length && policies.length === 1) {
      updateContract(policies[0]);
    }
  }, [policies, updateContract]);

  const updateIsDisabledInContracts = (date = null) => {
    handleUpdateDisabledInContracts(
      date,
      existingContracts,
      setExistingContracts,
      contract,
      updateContract,
      setShowContractNotExistError,
    );
  };

  const updateAccDate = (newDate) => {
    setAccDate(newDate);
    setValue('accidentDate', newDate, true);
    updateIsDisabledInContracts(newDate);
    if (policies.length && policies.length === 1) {
      updateContract(policies[0]);
    }
  };

  const updateAccTime = () => (event) => {
    setAccTime(event.target.value);
    setValue('accidentTime', event.target.value, true);
  };

  const handleAccCountryChange = useCallback((accCountry) => {
    handleAccidentCountryChange(
      accCountry,
      setAccAddress,
      setValue,
      setAccVillages,
    );
  }, [setValue]);

  useEffect(() => {
    setValue('accCountry', findCountriesByISO3([ARMENIA_ISO3], countries), true);
    setValue('isAccInArmenia', true);
  }, [countries, setValue]);

  const handleVehicleDataChange = (data, name) => {
    changeVehicleData(data, name, setValue, setVehicles, vehicles);
  };

  const addNewVehicle = () => {
    addVehicle(
      vehicles,
      getRowSchema,
      setVehicles,
      setSchema,
      getSchema,
      register,
      triggerValidation,
      setValue,
    );
  };

  const deleteVehicle = (id) => {
    const updatedVehicles = filter(vehicles, (car) => car.id !== id);
    setVehicles(updatedVehicles);
    setValue('vehicles', updatedVehicles, true);
    setSchema(getSchema(updatedVehicles));

    unregister(`stateNumber_${id}`);
    unregister(`manufacturer_${id}`);
    unregister(`model_${id}`);
  };

  const handleAccStateChange = (accState) => {
    changeAccidentState(
      setAccAddress,
      accAddress,
      accState,
      setValue,
      dispatch,
    );
  };

  const handleAccVillageChange = (accVillage) => {
    setAccAddress({
      ...accAddress,
      accVillage,
    });
    setValue('accVillage', accVillage, true);
  };

  const handleStreetChange = (target) => {
    setValue(target.name, target.value, true);
  };

  const handleDamageChange = (val) => {
    setDamageLevel(val);
    setCarAddress(CAR_INITIAL_ADDRESS);
    setValue('carCountry', null);
    setValue('isCarInArmenia', false);
    setValue('carState', null);
    setValue('carCityVillage', null);
    setValue('carStreet', '');
  };

  const handleIsExistOtherCarChange = (val) => {
    setIsExistOtherCar(val);

    if (val) {
      setVehicles([
        {
          id: 1,
          stateNumber: '',
          manufacturer: null,
          model: null,
        },
      ]);
    } else {
      setVehicles([]);
      setValue('vehicles', []);
    }
  };

  const handleCarCountryChange = (carCountry) => {
    changeCarCountry(
      carCountry,
      setCarAddress,
      CAR_INITIAL_ADDRESS,
      setValue,
      RAStates,
      dispatch,
      setCarVillages,
    );
  };

  const handleCarStateChange = (carState) => {
    changeCarState(setCarAddress, carAddress, setValue, carState, dispatch);
  };

  const handleCarVillageChange = (carVillage) => {
    setCarAddress({
      ...carAddress,
      carVillage,
    });
    setValue('carVillage', carVillage, true);
  };

  const handleDamagedPartsChange = ({ value }) => {
    const valToSet = value.replace(/^\s+|\s+$/g, '').length > 0 ? value : '';
    setDamagedParts(valToSet);
    setValue('damagedParts', valToSet, true);
  };

  const handleAccidentDetailsChange = ({ value }) => {
    const valToSet = value.replace(/^\s+|\s+$/g, '').length > 0 ? value : '';
    setAccidentDetails(valToSet);
    setValue('accidentDetails', valToSet, true);
  };

  const handleProceedingChange = (val) => {
    setProceedingStatus(val);
  };

  const onSubmit = (data) => {
    dispatch(
      updateCascoForm({
        name: 'accidentInfo',
        data: { ...data, carList: getValues().vehicles },
      }),
    );
    goNext(step);
  };

  const getLabelWithTranslation = useCallback(
    (option) => getMenuLabelWithTranslation(option, i18n),
    [i18n],
  );

  return (
    <div ref={scrollTo} className={styles.formContainer}>
      <ClaimStepNavigationWrapper
        clearError={clearError}
        errors={errors}
        isNextDisabled={showContractNotExistError}
        handleSubmit={handleSubmit}
        setStepNavigationBox={setStepNavigationBox}
        isDeviceLgSize={isDeviceLgSize}
        onSubmit={onSubmit}
        back={goPrevious}
        dependencies={[JSON.stringify(errors), isDeviceLgSize, showContractNotExistError]}
      />
      <FormSection title="claim.accInfo.cascoAccInfo" icon={AccidentInfoIcon}>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6}>
            <DateInputFieldWithValidation
              formProps={{ errors, clearError }}
              labelKey="claim.accInfo.date"
              name="accidentDate"
              value={accDate || null}
              handleChange={(date) => {
                // eslint-disable-next-line no-restricted-globals
                if (date instanceof Date && !isNaN(date)) {
                  updateAccDate(date);
                }
              }}
              maxDate={new Date()}
              required
            />
            {!errors.accDate && showContractNotExistError && (
              <p className={classes.errorText}>
                {t('claim.accInfo.cascoInSelectedDateContractNotExist')}
              </p>
            )}
          </Grid>
          <Grid item xs={12} sm={4}>
            <TimeInputWithValidation
              formProps={{ errors, clearError }}
              handleTimeChange={updateAccTime()}
              value={accTime}
              name="accidentTime"
              label={t('claim.accInfo.time')}
            />
          </Grid>
        </Grid>
      </FormSection>
      <FormSection title="claim.accInfo.accAddress" icon={AccidentPlaceIcon}>
        <Grid container spacing={4}>
          <Grid
            item
            xs={12}
            md={!isEmpty(accAddress.accState) && accAddress.isAccInArmenia && !accIsYerevan ? 4 : 6}
          >
            <SelectWithValidation
              formProps={{
                errors,
                clearError,
              }}
              value={accAddress.accCountry || ''}
              options={countries}
              getMenuLabel={getLabelWithTranslation}
              onChange={handleAccCountryChange}
              label="insurance.country"
              required
              name="accCountry"
            />
          </Grid>
          {accAddress.isAccInArmenia && (
            <ArmenianRegionSelectOptions
              cityVillages={accVillages}
              isYerevan={accIsYerevan}
              onCityVillageChange={handleAccVillageChange}
              errors={errors}
              clearError={clearError}
              states={RAStates}
              form={{
                cityVillage: {
                  value: accAddress.accVillage || {},
                  label: 'contactInfo.cityVillage',
                  name: 'accVillage',
                },
                state: {
                  value: accAddress.accState || {},
                  label: 'contactInfo.state',
                  name: 'accState',
                },
              }}
              onStateChange={handleAccStateChange}
            />
          )}
          <Grid item xs={12}>
            <TextInputFieldWithValidation
              formProps={{ errors, clearError }}
              labelKey="claim.accInfo.address"
              name="accStreet"
              handleChange={(e) => handleStreetChange(e.target)}
              register={register}
              required
            />
          </Grid>
        </Grid>
      </FormSection>
      <FormSection title="claim.accInfo.damageLevel" icon={RunAndDriveIcon}>
        <Grid container spacing={4}>
          {DAMAGED_CAR_OPTIONS.map(({ value, label }) => (
            <Grid key={label} item>
              <MainRadio
                name="damageLevel"
                key={label}
                labelKey={label}
                value={value}
                checked={value === damageLevel}
                onChange={({ e }) => handleDamageChange(Number(e.target.value))}
              />
            </Grid>
          ))}
        </Grid>
      </FormSection>
      {damageLevel === DAMAGED_CAR_OPTIONS[1].value && (
        <FormSection title="claim.accInfo.carLocation" icon={AccidentPlaceIcon}>
          <Grid container spacing={4}>
            <Grid
              item
              xs={12}
              md={
                !isEmpty(carAddress.carState) && carAddress.isCarInArmenia && !carIsYerevan ? 4 : 6
              }
            >
              <SelectWithValidation
                formProps={{
                  errors,
                  clearError,
                }}
                value={carAddress.carCountry || ''}
                options={countries}
                getMenuLabel={getLabelWithTranslation}
                onChange={handleCarCountryChange}
                label="insurance.country"
                required
                name="carCountry"
              />
            </Grid>
            {carAddress.isCarInArmenia && (
              <ArmenianRegionSelectOptions
                cityVillages={carVillages}
                isYerevan={carIsYerevan}
                onCityVillageChange={handleCarVillageChange}
                cityVillagesLoading={false}
                errors={errors}
                clearError={clearError}
                states={RAStates}
                form={{
                  cityVillage: {
                    value: carAddress.carVillage || {},
                    label: 'contactInfo.cityVillage',
                    name: 'carVillage',
                  },
                  state: {
                    value: carAddress.carState || {},
                    label: 'contactInfo.state',
                    name: 'carState',
                  },
                }}
                onStateChange={handleCarStateChange}
              />
            )}
            <Grid item xs={12}>
              <TextInputFieldWithValidation
                formProps={{ errors, clearError }}
                labelKey="claim.accInfo.address"
                name="carStreet"
                handleChange={(e) => handleStreetChange(e.target)}
                register={register}
                required
              />
            </Grid>
          </Grid>
        </FormSection>
      )}
      {accDate && !errors.accidentDate && !showContractNotExistError && (
        <ExistingContracts
          existingContracts={existingContracts}
          updateContract={updateContract}
          errors={errors}
          contract={contract}
          classes={classes}
        />
      )}
      <FormSection title="claim.accInfo.damageDescription" icon={DamageInfoIcon}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <TextInputFieldWithValidation
              formProps={{ errors, clearError }}
              labelKey="claim.accInfo.cascoDamageAmount"
              name="damageAmount"
              handleChange={(e) => {
                const currentValue = e.target.value;
                const fixedValue = currentValue.replace(NON_DIGITS_PATTERN, '');
                setDamageAmount(Number(fixedValue));
              }}
              value={damageAmount ? damageAmount.toLocaleString('en-US') : ''}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextInputFieldWithValidation
              formProps={{ errors, clearError }}
              labelKey="claim.accInfo.damagedParts"
              name="damagedParts"
              handleChange={(e) => handleDamagedPartsChange(e.target)}
              value={damagedParts}
              required
              multiline
              minRows={4}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInputFieldWithValidation
              formProps={{ errors, clearError }}
              labelKey="claim.accInfo.accidentDetails"
              name="accidentDetails"
              handleChange={(e) => handleAccidentDetailsChange(e.target)}
              value={accidentDetails}
              required
              multiline
              minRows={4}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </FormSection>
      <FormSection title="claim.accInfo.proceeding" icon={ProceedingsIcon}>
        <Validation name="proceedingStatus" errors={errors}>
          <Grid container spacing={2}>
            {PROCEEDINGS.map(({ value, label }) => (
              <Grid key={label} item md={12}>
                <MainRadio
                  name="proceedingStatus"
                  labelKey={`claim.accInfo.${label}`}
                  value={value}
                  checked={value === proceedingStatus}
                  onChange={() => handleProceedingChange(value)}
                />
              </Grid>
            ))}
          </Grid>
        </Validation>
      </FormSection>
      <FormSection title="claim.accInfo.isExistOtherCar" icon={OtherCarsIcon}>
        <Grid container spacing={2}>
          {ACCIDENT_OTHER_CARS_OPTIONS.map(({ value, label }) => (
            <Grid key={label} item>
              <MainRadio
                name="isExistOtherCar"
                labelKey={label}
                value={value}
                checked={value === isExistOtherCar}
                onChange={() => handleIsExistOtherCarChange(value)}
              />
            </Grid>
          ))}
        </Grid>
      </FormSection>
      {isExistOtherCar && (
        <FormSection title="personalClaim.accInfo.vehicles" icon={RunAndDriveIcon}>
          {vehicles.map((vehicle, index) => (
            <VehicleRow
              key={`vehicleRow_${vehicle.id}`}
              subTitleNum={index + 1}
              vehicleData={vehicle}
              onVehicleDataChange={handleVehicleDataChange}
              isDeletable={vehicles.length > 1}
              deleteVehicle={deleteVehicle}
              manufacturers={manufacturers}
              errors={errors}
              clearError={clearError}
              register={register}
            />
          ))}
          <div className={classes.plusIcon} onClick={() => addNewVehicle()} role="presentation">
            <Icon name="plus circle" className={classes.addIcon} />
            <p>{t('personalClaim.accInfo.addVehicle')}</p>
          </div>
        </FormSection>
      )}
    </div>
  );
};

CascoAccidentInfoStep.propTypes = {
  step: PropTypes.shape({
    n: PropTypes.number.isRequired,
  }).isRequired,
  goNext: PropTypes.func.isRequired,
  goPrevious: PropTypes.func.isRequired,
  params: PropTypes.shape({
    setStepNavigationBox: PropTypes.func.isRequired,
    scrollTo: PropTypes.func.isRequired,
    formLanguage: PropTypes.string,
  }).isRequired,
};

export default CascoAccidentInfoStep;
